"use client"

import * as React from "react"
import {
  ThemeProvider as NextThemesProvider,
  useTheme as useNextTheme,
} from "next-themes"

import { cn } from "@/lib/utils"

export interface ThemeTransitionOrigin {
  x: number
  y: number
}

type ThemeTransition = {
  to: "light" | "dark"
  origin: ThemeTransitionOrigin
  id: number
}

type ThemeContextValue = {
  toggleTheme: (origin?: ThemeTransitionOrigin) => void
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(
  undefined
)
const VEIL_DURATION_MS = 650

export function ThemeProvider({
  children,
  ...props
}: React.ComponentProps<typeof NextThemesProvider>) {
  return (
    <NextThemesProvider {...props}>
      <ThemeVeilProvider>{children}</ThemeVeilProvider>
    </NextThemesProvider>
  )
}

function ThemeVeilProvider({ children }: { children: React.ReactNode }) {
  const { resolvedTheme, setTheme } = useNextTheme()
  const [transition, setTransition] = React.useState<ThemeTransition | null>(
    null
  )
  const lockedRef = React.useRef(false)
  const flipTimerRef = React.useRef<number | null>(null)
  const endTimerRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    return () => {
      if (flipTimerRef.current) window.clearTimeout(flipTimerRef.current)
      if (endTimerRef.current) window.clearTimeout(endTimerRef.current)
    }
  }, [])

  const toggleTheme = React.useCallback<ThemeContextValue["toggleTheme"]>(
    (origin) => {
      if (lockedRef.current) return

      const next = resolvedTheme === "dark" ? "light" : "dark"
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setTheme(next)
        return
      }

      lockedRef.current = true
      const nextTransition: ThemeTransition = {
        to: next,
        origin: origin ?? { x: window.innerWidth - 48, y: 48 },
        id: Date.now(),
      }
      setTransition(nextTransition)

      flipTimerRef.current = window.setTimeout(() => {
        setTheme(next)
        flipTimerRef.current = null
      }, VEIL_DURATION_MS * 0.5)

      endTimerRef.current = window.setTimeout(() => {
        setTransition(null)
        lockedRef.current = false
        endTimerRef.current = null
      }, VEIL_DURATION_MS + 60)
    },
    [resolvedTheme, setTheme]
  )

  return (
    <ThemeContext.Provider value={{ toggleTheme }}>
      {children}
      <ThemeVeil transition={transition} />
    </ThemeContext.Provider>
  )
}

function ThemeVeil({ transition }: { transition: ThemeTransition | null }) {
  if (!transition) return null

  const { to, origin, id } = transition
  const width = typeof window === "undefined" ? 1920 : window.innerWidth
  const height = typeof window === "undefined" ? 1080 : window.innerHeight
  const dx = Math.max(origin.x, width - origin.x)
  const dy = Math.max(origin.y, height - origin.y)
  const radius = Math.ceil(Math.sqrt(dx * dx + dy * dy)) + 40
  const center = `${origin.x}px ${origin.y}px`

  return (
    <div
      key={id}
      aria-hidden="true"
      className={cn(
        "pointer-events-none fixed inset-0 z-100",
        to === "dark" && "dark"
      )}
      style={{
        background:
          to === "dark"
            ? "radial-gradient(circle at 50% 9%, color-mix(in oklab, var(--primary) 14%, transparent), transparent 30rem), linear-gradient(to bottom, color-mix(in oklab, var(--primary) 4%, transparent), transparent 70%), var(--background)"
            : "radial-gradient(circle at 50% 30%, oklch(1 0 0), oklch(0.96 0.025 265) 58%)",
        animation: `theme-veil-expand ${VEIL_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1) forwards`,
        clipPath: `circle(0px at ${center})`,
        ["--veil-radius" as string]: `${radius}px`,
        ["--veil-center" as string]: center,
      }}
    />
  )
}

export function useThemeTransition() {
  return React.useContext(ThemeContext) ?? { toggleTheme: () => {} }
}
